import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useFormDraft } from "@/hooks/useFormDraft";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { 
  Building2, 
  Phone, 
  Globe, 
  MapPin, 
  FileText, 
  Tag, 
  ArrowLeft, 
  Loader2,
  Check,
  AlertCircle
} from "lucide-react";

import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationSelector from "@/components/LocationSelector";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { LocationValue } from "@/types";

// Company categories - matching PazaryeriPage.tsx
const COMPANY_CATEGORIES = [
  "usta",
  "muhendislik_hizmetleri",
  "beton",
  "insaat_malzemesi",
  "pen_isleri",
  "seramik",
  "vitrifiye",
  "tesisat_malzemesi",
  "elektrik_malzemesi",
  "ankastre_beyaz_esya",
  "iklimlendirme_klima",
  "dogalgaz",
  "peyzaj_cevre_duzenlemesi",
  "hafriyat",
];

// Category emoji mapping - matching PazaryeriPage.tsx
const categoryEmojis: Record<string, string> = {
  "usta": "🧰",
  "muhendislik_hizmetleri": "📐",
  "beton": "🏗️",
  "insaat_malzemesi": "📦",
  "pen_isleri": "🪟",
  "seramik": "🧱",
  "vitrifiye": "🚿",
  "tesisat_malzemesi": "🔧",
  "elektrik_malzemesi": "💡",
  "ankastre_beyaz_esya": "🍽️",
  "iklimlendirme_klima": "❄️",
  "dogalgaz": "🔥",
  "peyzaj_cevre_duzenlemesi": "🌳",
  "hafriyat": "🚜",
};

// Form validation schema
const addCompanySchema = z.object({
  name: z.string().min(2, "Company name must be at least 2 characters").max(100, "Company name must be at most 100 characters"),
  category: z.string().min(1, "Category selection is required"),
  description: z.string().min(10, "Description must be at least 10 characters").max(500, "Description must be at most 500 characters"),
  address: z.string().min(5, "Address must be at least 5 characters").max(200, "Address must be at most 200 characters"),
  phone: z.string().optional(),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  location: z.object({
    country: z.object({
      value: z.string(),
      label: z.string()
    }).nullable().refine(val => val !== null, { message: "Country selection is required" }),
    city: z.object({
      value: z.string(),
      label: z.string()
    }).nullable().refine(val => val !== null, { message: "City selection is required" })
  })
});

type AddCompanyFormData = z.infer<typeof addCompanySchema>;

const AddCompanyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<AddCompanyFormData>({
    resolver: zodResolver(addCompanySchema),
    defaultValues: {
      name: "",
      category: "",
      description: "",
      address: "",
      phone: "",
      website: "",
      location: {
        country: null,
        city: null
      }
    }
  });

  // Persist in-progress company input across background / app kill.
  const { clearDraft } = useFormDraft(form, { key: "add-company" });

  const onSubmit = async (data: AddCompanyFormData) => {
    if (!user) {
      toast.error(t('errors.unauthorized'));
      return;
    }

    setIsLoading(true);
    
    try {
      // For now, we'll set latitude and longitude to 0
      // In a production app, you might want to geocode the address
      const { error } = await supabase
        .from('nearby_companies')
        .insert({
          name: data.name,
          category: data.category,
          description: data.description,
          address: data.address,
          city: data.location.city!.label,
          country: data.location.country!.label,
          phone: data.phone || null,
          website: data.website || null,
          latitude: 0, // TODO: Geocode address to get coordinates
          longitude: 0,
          ad: false, // New companies don't have ads by default
          ad_start_date: null,
          ad_end_date: null
        });

      if (error) {
        
        toast.error(t('errors.save_error') + ": " + error.message);
        return;
      }

      setIsSubmitted(true);
      clearDraft();
      toast.success(t('common.success'));
      
      // Navigate back to marketplace after a short delay
      setTimeout(() => {
        navigate('/pazaryeri');
      }, 2000);

    } catch (error) {
      
      toast.error(t('errors.error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  // If successfully submitted, show success message
  if (isSubmitted) {
    return (
      <div className="page-container h-full flex flex-col bg-gray-25">
        <AppHeader
          title={t('common.success')}
          showBackButton
        />

        <main className="page-content flex-1 overflow-y-auto pt-[var(--header-safe-height)]">
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="bg-success-50 rounded-full p-6 mb-6">
              <Check className="h-12 w-12 text-success-600" />
            </div>
            <h2 className="text-[22px] font-bold mb-3 text-gray-900">
              {t('common.success')}!
            </h2>
            <p className="text-[15px] mb-6 text-gray-600">
              {t('notifications.operation_successful')}
            </p>
            <Button
              onClick={() => navigate('/pazaryeri')}
              className="bg-brand-600 hover:bg-brand-700 text-white font-semibold"
            >
              {t('navigation.marketplace')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page-container h-full flex flex-col bg-gray-25">
      <AppHeader
        title={t('common.add') + " " + t('common.company', "Company")}
        showBackButton
      />

      <main className="page-content flex-1 overflow-y-auto pt-[var(--header-safe-height)]">
        <div className="p-4 max-w-2xl mx-auto space-y-4">
          {/* Info Banner */}
          <div className="flex items-start gap-3 bg-brand-50 border border-brand-100 rounded-xl p-4">
            <Building2 className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[13px] font-semibold text-brand-900">{t('marketplace.free_listing_info')}</p>
              <p className="text-[12px] text-brand-700 mt-0.5">
                {t('marketplace.add_company_description', "Add your company to our marketplace for free and connect with potential customers.")}
              </p>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-[17px] font-bold text-gray-900 mb-1">{t('common.details')}</h2>
            <p className="text-[12px] text-gray-500 mb-5">
              {t('marketplace.company_form_description', "Enter your company's detailed information")}
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* Company Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5" />
                        {t('common.name', "Company Name")} *
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('marketplace.company_name_placeholder', "Enter your company name")}
                          className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Category */}
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" />
                        {t('marketplace.categories')} *
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading}>
                        <FormControl>
                          <SelectTrigger className="bg-white border-gray-200 text-gray-900 rounded-md">
                            <SelectValue placeholder={t('marketplace.category_select')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border-gray-200 rounded-md">
                          {COMPANY_CATEGORIES.map((category) => (
                            <SelectItem key={category} value={category} className="focus:bg-brand-50 text-gray-700">
                              <span className="flex items-center gap-2">
                                <span>{categoryEmojis[category] || '🏢'}</span>
                                <span>{t(`marketplace.business_categories.${category}`)}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" />
                        {t('common.description', "Description")} *
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('marketplace.description_placeholder', "Provide detailed information about your company...")}
                          className="min-h-[100px] bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Address */}
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {t('common.address', "Address")} *
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('marketplace.address_placeholder', "Enter your company's full address...")}
                          className="min-h-[80px] bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Location */}
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5" />
                        {t('common.location', "Location")} *
                      </FormLabel>
                      <FormControl>
                        <div className="bg-white border border-gray-200 rounded-md">
                          <LocationSelector
                            value={field.value as LocationValue}
                            onChange={field.onChange}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Phone */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        {t('common.phone', "Phone")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('marketplace.phone_placeholder', "Enter your phone number")}
                          className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Website */}
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600 flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5" />
                        {t('common.website', "Website")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://www.example.com"
                          className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-md"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-[11px] text-error-600" />
                    </FormItem>
                  )}
                />

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/pazaryeri')}
                    disabled={isLoading}
                    className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-brand-600 hover:bg-brand-700 text-white font-semibold"
                  >
                    {isLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Building2 className="mr-2 h-4 w-4" />
                    )}
                    {t('common.add')}
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Additional Info */}
          <div className="flex items-start gap-3 bg-warning-50 border border-warning-300 rounded-xl p-4 mb-6">
            <AlertCircle className="h-5 w-5 text-warning-600 mt-0.5 flex-shrink-0" />
            <div className="text-[12px] text-gray-700">
              <p className="font-semibold mb-1 text-gray-900">{t('common.info')}:</p>
              <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                <li>{t('marketplace.info_accurate', "All information must be accurate and up-to-date")}</li>
                <li>{t('marketplace.info_review', "Your company will be published after review")}</li>
                <li>{t('marketplace.info_consequences', "Providing false information may result in account suspension")}</li>
                <li>{t('marketplace.info_advertising', "Contact us separately for advertising opportunities")}</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddCompanyPage; 